import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';

import { BACKGROUND_GRADIENTS, CARD_COLORS, CARD_ILLUSTRATIONS, RENDERER_WALLET_KEYS, WALLET_KEYS } from '../../schemas/endingConstants.js';
import { isNonEmptyString, shapeIssue } from './_helpers/shapeIssues.js';

const err = shapeIssue('rich-ending / wallet integrity');

interface NodeLike {
  id?: unknown;
  nodeType?: unknown;
  walletUpdates?: unknown;
  walletKey?: unknown;
  exitRoutes?: unknown;
  options?: unknown;
  endingSummary?: unknown;
}

interface InnerLike {
  nodes?: unknown;
}

const KNOWN_GRADIENTS = new Set<string>(BACKGROUND_GRADIENTS);
const KNOWN_WALLET_KEYS = new Set<string>(WALLET_KEYS);
const RENDERER_KEYS = new Set<string>(RENDERER_WALLET_KEYS);
const KNOWN_CARD_COLORS = new Set<string>(CARD_COLORS);
const KNOWN_CARD_ILLUSTRATIONS = new Set<string>(CARD_ILLUSTRATIONS);

/**
 * content.richEnding / content.wallet / content.walletEnding — validates the
 * result-quiz mechanisms (RIASEC / MBTI / work values).
 *
 * `walletUpdates` (any node) saves wallet items on visit. An `ending:wallet`
 * node additionally renders the app's own result screen from its single string
 * wallet entry (or the one named by `walletKey`). A rich ending
 * (`nodeType: "ending:rich"`) must carry a fully-formed `options` block
 * (transcriptSummary, hero, content, cards). Both ending kinds are terminal
 * (no exitRoutes).
 *
 * The rule is inert for content that uses none of these mechanisms.
 */
export const richEndingShape: Rule<unknown> = {
  code: 'content.richEnding',
  check: (input): ValidationIssue[] => {
    const data = input as InnerLike;
    const nodes: NodeLike[] = Array.isArray(data.nodes) ? (data.nodes as NodeLike[]) : [];
    const issues: ValidationIssue[] = [];

    nodes.forEach((node, i) => {
      const isRich = node.nodeType === 'ending:rich';
      const isWalletEnding = node.nodeType === 'ending:wallet';
      const hasWallet = node.walletUpdates !== undefined && node.walletUpdates !== null;
      if (!isRich && !isWalletEnding && !hasWallet) {
        return;
      }

      const id = node.id;

      // --- walletUpdates shape (wherever it appears) ---
      const stringKeys: string[] = [];
      if (hasWallet) {
        const base: (string | number)[] = ['nodes', i, 'walletUpdates'];
        const w = node.walletUpdates;
        if (typeof w !== 'object' || Array.isArray(w)) {
          issues.push(err(
            'content.wallet.shape',
            `Node ${id ?? '?'}: walletUpdates must be an object mapping a wallet key to a result string`,
            base,
            id,
            w,
          ));
        }
        else {
          const entries = Object.entries(w as Record<string, unknown>);
          if (entries.length === 0) {
            issues.push(err(
              'content.wallet.empty',
              `Node ${id ?? '?'}: walletUpdates is empty — nothing is saved to the wallet`,
              base,
              id,
              w,
              'warning',
            ));
          }
          for (const [key, val] of entries) {
            if (!KNOWN_WALLET_KEYS.has(key)) {
              issues.push(err(
                'content.wallet.unknownKey',
                `Node ${id ?? '?'}: walletUpdates key '${key}' is not read by the runtime (expected one of ${[...KNOWN_WALLET_KEYS].join(', ')})`,
                [...base, key],
                id,
                key,
                'warning',
              ));
            }
            if (!isNonEmptyString(val)) {
              issues.push(err(
                'content.wallet.value',
                `Node ${id ?? '?'}: walletUpdates.${key} must be a non-empty result string`,
                [...base, key],
                id,
                val,
              ));
            }
            else {
              stringKeys.push(key);
            }
          }
        }
      }

      // --- ending:wallet — app-rendered result screen ---
      if (isWalletEnding) {
        if (node.exitRoutes !== undefined && node.exitRoutes !== null) {
          issues.push(err(
            'content.walletEnding.exitRoutes',
            `Wallet ending '${id ?? '?'}' has exitRoutes; ending nodes must be terminal`,
            ['nodes', i, 'exitRoutes'],
            id,
            node.exitRoutes,
          ));
        }

        let resolvedKey: string | undefined;
        if (node.walletKey !== undefined) {
          if (!isNonEmptyString(node.walletKey) || !stringKeys.includes(node.walletKey)) {
            issues.push(err(
              'content.walletEnding.walletKey',
              `Wallet ending '${id ?? '?'}': walletKey must name one of the node's string walletUpdates entries (${stringKeys.join(', ') || 'none'})`,
              ['nodes', i, 'walletKey'],
              id,
              node.walletKey,
            ));
          }
          else {
            resolvedKey = node.walletKey;
          }
        }
        else if (stringKeys.length === 1) {
          resolvedKey = stringKeys[0];
        }
        else if (stringKeys.length === 0) {
          issues.push(err(
            'content.walletEnding.updates',
            `Wallet ending '${id ?? '?'}' needs walletUpdates with a string entry to supply the result value`,
            ['nodes', i, 'walletUpdates'],
            id,
            node.walletUpdates,
          ));
        }
        else {
          issues.push(err(
            'content.walletEnding.walletKey',
            `Wallet ending '${id ?? '?'}' has multiple string walletUpdates entries (${stringKeys.join(', ')}); set walletKey to disambiguate`,
            ['nodes', i, 'walletUpdates'],
            id,
            stringKeys,
          ));
        }

        if (resolvedKey && KNOWN_WALLET_KEYS.has(resolvedKey) && !RENDERER_KEYS.has(resolvedKey)) {
          issues.push(err(
            'content.walletEnding.renderer',
            `Wallet ending '${id ?? '?'}': key '${resolvedKey}' has no dedicated result renderer — the app shows a generic fallback screen (dedicated: ${[...RENDERER_KEYS].join(', ')})`,
            ['nodes', i, 'walletUpdates', resolvedKey],
            id,
            resolvedKey,
            'warning',
          ));
        }

        for (const dead of ['options', 'endingSummary'] as const) {
          if (node[dead] !== undefined && node[dead] !== null) {
            issues.push(err(
              'content.walletEnding.ignored',
              `Wallet ending '${id ?? '?'}': '${dead}' is ignored on ending:wallet nodes — the app renders the result screen from the wallet value`,
              ['nodes', i, dead],
              id,
              node[dead],
              'warning',
            ));
          }
        }
      }

      if (!isRich) {
        return;
      }

      // --- rich endings are terminal ---
      if (node.exitRoutes !== undefined && node.exitRoutes !== null) {
        issues.push(err(
          'content.richEnding.exitRoutes',
          `Rich ending '${id ?? '?'}' has exitRoutes; ending nodes must be terminal`,
          ['nodes', i, 'exitRoutes'],
          id,
          node.exitRoutes,
        ));
      }

      // --- options block ---
      const base: (string | number)[] = ['nodes', i, 'options'];
      const opts = node.options;
      if (typeof opts !== 'object' || opts === null || Array.isArray(opts)) {
        issues.push(err(
          'content.richEnding.options',
          `Rich ending '${id ?? '?'}' must have an 'options' object (transcriptSummary, hero, content, cards)`,
          base,
          id,
          opts,
        ));
        return;
      }
      const o = opts as Record<string, unknown>;

      if (!isNonEmptyString(o.transcriptSummary)) {
        issues.push(err(
          'content.richEnding.transcriptSummary',
          `Rich ending '${id ?? '?'}': options.transcriptSummary must be a non-empty string`,
          [...base, 'transcriptSummary'],
          id,
          o.transcriptSummary,
        ));
      }

      // hero
      const hero = o.hero as Record<string, unknown> | undefined;
      if (typeof hero !== 'object' || hero === null || Array.isArray(hero)) {
        issues.push(err(
          'content.richEnding.hero',
          `Rich ending '${id ?? '?'}': options.hero must be an object (title, breadcrumb, backgroundGradient, illustration)`,
          [...base, 'hero'],
          id,
          hero,
        ));
      }
      else {
        for (const k of ['title', 'breadcrumb', 'illustration'] as const) {
          if (!isNonEmptyString(hero[k])) {
            issues.push(err(
              'content.richEnding.hero',
              `Rich ending '${id ?? '?'}': options.hero.${k} must be a non-empty string`,
              [...base, 'hero', k],
              id,
              hero[k],
            ));
          }
        }
        if (!isNonEmptyString(hero.backgroundGradient)) {
          issues.push(err(
            'content.richEnding.hero',
            `Rich ending '${id ?? '?'}': options.hero.backgroundGradient must be a non-empty string`,
            [...base, 'hero', 'backgroundGradient'],
            id,
            hero.backgroundGradient,
          ));
        }
        else if (!KNOWN_GRADIENTS.has(hero.backgroundGradient as string)) {
          issues.push(err(
            'content.richEnding.gradient',
            `Rich ending '${id ?? '?'}': backgroundGradient '${hero.backgroundGradient}' is not a known gradient (${[...KNOWN_GRADIENTS].join(', ')})`,
            [...base, 'hero', 'backgroundGradient'],
            id,
            hero.backgroundGradient,
            'warning',
          ));
        }
      }

      // content
      const content = o.content as Record<string, unknown> | undefined;
      if (typeof content !== 'object' || content === null || Array.isArray(content)) {
        issues.push(err(
          'content.richEnding.content',
          `Rich ending '${id ?? '?'}': options.content must be an object (title, description)`,
          [...base, 'content'],
          id,
          content,
        ));
      }
      else {
        for (const k of ['title', 'description'] as const) {
          if (!isNonEmptyString(content[k])) {
            issues.push(err(
              'content.richEnding.content',
              `Rich ending '${id ?? '?'}': options.content.${k} must be a non-empty string`,
              [...base, 'content', k],
              id,
              content[k],
            ));
          }
        }
      }

      // cards
      const cards = o.cards;
      if (!Array.isArray(cards) || cards.length === 0) {
        issues.push(err(
          'content.richEnding.cards',
          `Rich ending '${id ?? '?'}': options.cards must be a non-empty array`,
          [...base, 'cards'],
          id,
          cards,
        ));
      }
      else {
        cards.forEach((cardRaw, ci) => {
          const card = cardRaw as Record<string, unknown> | null;
          if (typeof card !== 'object' || card === null || Array.isArray(card)) {
            issues.push(err(
              'content.richEnding.cards',
              `Rich ending '${id ?? '?'}': options.cards[${ci}] must be an object (title, color, illustration)`,
              [...base, 'cards', ci],
              id,
              card,
            ));
            return;
          }
          for (const k of ['title', 'color', 'illustration'] as const) {
            if (!isNonEmptyString(card[k])) {
              issues.push(err(
                'content.richEnding.cards',
                `Rich ending '${id ?? '?'}': options.cards[${ci}].${k} must be a non-empty string`,
                [...base, 'cards', ci, k],
                id,
                card[k],
              ));
            }
          }
          if (isNonEmptyString(card.color) && !KNOWN_CARD_COLORS.has(card.color)) {
            issues.push(err(
              'content.richEnding.cardColor',
              `Rich ending '${id ?? '?'}': options.cards[${ci}].color '${card.color}' is not a card color the app styles (${[...KNOWN_CARD_COLORS].join(', ')})`,
              [...base, 'cards', ci, 'color'],
              id,
              card.color,
              'warning',
            ));
          }
          if (isNonEmptyString(card.illustration) && !KNOWN_CARD_ILLUSTRATIONS.has(card.illustration)) {
            issues.push(err(
              'content.richEnding.cardIllustration',
              `Rich ending '${id ?? '?'}': options.cards[${ci}].illustration '${card.illustration}' is not a card icon (${[...KNOWN_CARD_ILLUSTRATIONS].join(', ')}) — the app falls back to a generic heart. Note: the hero illustration set is different and much larger.`,
              [...base, 'cards', ci, 'illustration'],
              id,
              card.illustration,
              'warning',
            ));
          }
        });
      }
    });

    return issues;
  },
};
